/* AI Score panel for the Lead Detail / Report page.
 *
 * Shows the latest score breakdown (motivation, urgency, equity,
 * contactability) with the AI's reasoning + next-action recommendation.
 * Has a "Score with AI" button that calls /api/leads/{id}/ai-score and
 * updates the lead inline.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Flame, Snowflake, TrendingUp } from "lucide-react";
import { openUpgradeModal } from "@/components/UpgradeToProModal";

export default function AIScorePanel({ lead, onUpdate }) {
  const [scoring, setScoring] = useState(false);
  const score = lead?.ai_score;
  const label = lead?.ai_score_label;

  const runScore = async () => {
    setScoring(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/ai-score`);
      toast.success(`Scored ${data.score}/100 — ${data.label.toUpperCase()}`);
      onUpdate?.({
        ai_score: data.score,
        ai_score_label: data.label,
        ai_score_motivation: data.motivation,
        ai_score_urgency: data.urgency,
        ai_score_equity: data.equity,
        ai_score_contactability: data.contactability,
        ai_score_reasoning: data.reasoning,
        ai_score_next_action: data.next_action,
        ai_scored_at: data.scored_at,
      });
    } catch (e) {
      if (e?.response?.status === 402) {
        openUpgradeModal({ feature: "lead-scoring" });
      } else {
        toast.error(e?.response?.data?.detail || "Scoring failed");
      }
    } finally {
      setScoring(false);
    }
  };

  const tone =
    label === "hot"
      ? "bg-gradient-to-br from-rose-50 to-amber-50 border-rose-200"
      : label === "warm"
      ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200"
      : label === "cold"
      ? "bg-gradient-to-br from-slate-50 to-sky-50 border-slate-200"
      : "bg-slate-50 border-slate-200";

  const Icon = label === "hot" ? Flame : label === "cold" ? Snowflake : TrendingUp;
  const iconColor =
    label === "hot"
      ? "text-rose-600"
      : label === "warm"
      ? "text-amber-600"
      : label === "cold"
      ? "text-slate-500"
      : "text-emerald-600";

  return (
    <div className={`card border-2 ${tone} mb-5`} data-testid="ai-score-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon size={18} className={iconColor} />
          <div className="font-display font-bold text-lg">AI Lead Score</div>
          {typeof score === "number" && (
            <span
              className="font-display font-extrabold text-2xl ml-2 tabular-nums"
              data-testid="ai-score-number"
            >
              {score}
              <span className="text-sm font-normal text-stone-500">/100</span>
            </span>
          )}
          {label && (
            <span
              className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                label === "hot"
                  ? "bg-rose-600 text-white"
                  : label === "warm"
                  ? "bg-amber-500 text-white"
                  : "bg-slate-400 text-white"
              }`}
            >
              {label}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={runScore}
          disabled={scoring}
          data-testid="run-ai-score-btn"
          className="btn-money text-sm flex items-center gap-1.5"
        >
          {scoring ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {scoring ? "Scoring…" : score != null ? "Re-score" : "Score with AI"}
        </button>
      </div>

      {score != null ? (
        <>
          {/* Breakdown bars */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Bar label="Motivation" value={lead.ai_score_motivation} color="bg-rose-500" />
            <Bar label="Urgency" value={lead.ai_score_urgency} color="bg-amber-500" />
            <Bar label="Equity" value={lead.ai_score_equity} color="bg-emerald-500" />
            <Bar label="Reachable" value={lead.ai_score_contactability} color="bg-sky-500" />
          </div>

          {lead.ai_score_reasoning && (
            <div className="text-sm text-stone-700 leading-relaxed mb-2" data-testid="ai-score-reasoning">
              {lead.ai_score_reasoning}
            </div>
          )}

          {lead.ai_score_next_action && (
            <div
              className="mt-3 px-3 py-2 rounded-lg bg-white/70 border text-sm"
              data-testid="ai-score-next-action"
            >
              <span className="font-bold text-stone-900">Next action: </span>
              <span className="text-stone-700">{lead.ai_score_next_action}</span>
            </div>
          )}

          {lead.ai_scored_at && (
            <div className="text-[10px] text-stone-500 mt-2">
              Scored {new Date(lead.ai_scored_at).toLocaleString()}
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-stone-600">
          Get an instant 0–100 score on this lead. Claude analyzes motivation,
          urgency, equity, and contactability — and recommends your next move.
        </div>
      )}
    </div>
  );
}

function Bar({ label, value, color }) {
  const pct = typeof value === "number" ? Math.max(0, Math.min(100, (value / 25) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-[10px] uppercase font-bold text-stone-500 mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{value ?? 0}/25</span>
      </div>
      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
